/**
 * 단순 화면 부품. 디자인은 하지 않는다(디자인은 대표님이 따로, 공통/작업규칙 9절). 큰 글씨, 큰 버튼만.
 */
import { useEffect, useState, type ReactNode } from 'react';

export const box = { maxWidth: 560, margin: '40px auto', padding: '0 16px', fontFamily: 'system-ui, sans-serif', fontSize: 20, lineHeight: 1.6 } as const;

export function Page({ title, children }: { title: string; children: ReactNode }) {
  return (
    <main style={box}>
      <h1 style={{ fontSize: 32, margin: '0 0 24px' }}>{title}</h1>
      {children}
    </main>
  );
}

export function Field(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'password' | 'email' | 'tel';
  onBlur?: () => void;
  autoComplete?: string;
}) {
  return (
    <label style={{ display: 'block', margin: '0 0 16px' }}>
      <span style={{ display: 'block', marginBottom: 4 }}>{props.label}</span>
      <input
        value={props.value}
        type={props.type ?? 'text'}
        autoComplete={props.autoComplete}
        onChange={(e) => props.onChange(e.target.value)}
        onBlur={props.onBlur}
        style={{ width: '100%', boxSizing: 'border-box', fontSize: 20, padding: '10px 12px' }}
      />
    </label>
  );
}

export function Button(props: { children: ReactNode; onClick: () => void; primary?: boolean; disabled?: boolean }) {
  return (
    <button
      type="button"
      disabled={props.disabled}
      onClick={props.onClick}
      style={{
        fontSize: 20, padding: '12px 20px', margin: '0 8px 12px 0', cursor: 'pointer',
        background: props.primary ? '#1f4ea8' : '#fff', color: props.primary ? '#fff' : '#111',
        border: '1px solid #1f4ea8', borderRadius: 6, opacity: props.disabled ? 0.5 : 1
      }}
    >
      {props.children}
    </button>
  );
}

export function Message({ text, kind = 'error' }: { text: string | null; kind?: 'error' | 'info' }) {
  if (!text) return null;
  return <p role={kind === 'error' ? 'alert' : 'status'} style={{ color: kind === 'error' ? '#b00020' : '#1f4ea8' }}>{text}</p>;
}

export function Check({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label style={{ display: 'block', margin: '0 0 16px' }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ width: 22, height: 22, marginRight: 8 }} />
      {label}
    </label>
  );
}

/** 남은 시간 09:59, 09:58처럼 1초마다 줄어드는 시계(2026-11-02 대표님) */
export function Countdown({ seconds, startedAt, onEnd }: { seconds: number; startedAt: number; onEnd: () => void }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const left = Math.max(0, seconds - Math.floor((now - startedAt) / 1000));
  useEffect(() => {
    if (left === 0) onEnd();
  }, [left, onEnd]);
  const mm = String(Math.floor(left / 60)).padStart(2, '0');
  const ss = String(left % 60).padStart(2, '0');
  return <p style={{ fontVariantNumeric: 'tabular-nums' }}>남은 시간 {mm}:{ss}</p>;
}

/** 묻는 창(예: 동시 접속 정원이 찼을 때) */
export function Confirm({ text, ok, onOk, onCancel }: { text: string; ok: string; onOk: () => void; onCancel: () => void }) {
  return (
    <div role="dialog" style={{ border: '2px solid #1f4ea8', borderRadius: 8, padding: 16, margin: '16px 0' }}>
      <p>{text}</p>
      <Button primary onClick={onOk}>{ok}</Button>
      <Button onClick={onCancel}>취소</Button>
    </div>
  );
}

const BAD_FILE = '이 파일은 열 수 없어요. 등록증을 찍은 사진이나 PDF 파일로 올려 주시겠어요?';
const MAX_SIDE = 1600;

/** 그림을 긴 쪽 1600픽셀 이하 JPEG 글자로 만든다(서버도 같은 크기로 줄인다). */
function canvasToJpeg(draw: (ctx: CanvasRenderingContext2D) => void, width: number, height: number): string {
  const scale = Math.min(1, MAX_SIDE / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error(BAD_FILE);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.scale(scale, scale);
  draw(ctx);
  return canvas.toDataURL('image/jpeg', 0.85);
}

/**
 * PDF의 첫 장을 사진으로 바꾼다(등록증은 한 장, 2026-11-02 대표님 "pdf도 용량 낮춰서 저장").
 * PDF를 여는 도구(pdf.js, 6.4.299 고정)는 PDF를 고를 때만 불러온다. 첫 장 그림만 그린다(문서 속 양식과 스크립트는 다루지 않음).
 */
async function pdfToDataUrl(file: File): Promise<string> {
  const pdfjs = await import('pdfjs-dist');
  const worker = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  const task = pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()) });
  try {
    const doc = await task.promise;
    const page = await doc.getPage(1);
    const base = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({ scale: MAX_SIDE / Math.max(base.width, base.height) });
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(viewport.width);
    canvas.height = Math.round(viewport.height);
    await page.render({ canvas, viewport }).promise;
    return canvasToJpeg((ctx) => ctx.drawImage(canvas, 0, 0), canvas.width, canvas.height);
  } finally {
    await task.destroy();
  }
}

/** 등록증 파일을 서버로 보낼 글자로 바꾼다. 사진은 긴 쪽 1600픽셀로, PDF는 첫 장을 사진으로 바꿔 줄인다. */
export async function photoToDataUrl(file: File): Promise<string> {
  try {
    if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) return await pdfToDataUrl(file);
    if (!file.type.startsWith('image/') && !/\.(heic|heif)$/i.test(file.name)) throw new Error(BAD_FILE);
    const original = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result));
      r.onerror = () => reject(new Error(BAD_FILE));
      r.readAsDataURL(file);
    });
    if (file.size < 2 * 1024 * 1024) return original;
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error(BAD_FILE));
      img.src = original;
    });
    return canvasToJpeg((ctx) => ctx.drawImage(img, 0, 0), img.width, img.height);
  } catch {
    throw new Error(BAD_FILE);
  }
}

export function Link({ to, go, children }: { to: string; go: (path: string) => void; children: ReactNode }) {
  return (
    <a href={to} onClick={(e) => { e.preventDefault(); go(to); }} style={{ marginRight: 16, color: '#1f4ea8' }}>
      {children}
    </a>
  );
}
